import React, { useState, useRef, useEffect } from 'react';
import { TimelineItemType, TimelineNoteType } from 'types/TimelineTypes';
import { UserType } from '../../views/users/UserTypes';
import { useGlobalContext } from 'GlobalContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useModal } from 'components/modals/ModalContext';
import axios from 'axios';
import { saveData } from 'services/api/saveData';
import { formatTimelineDate } from 'services/utils/dateTimeUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStickyNote, faChevronDown, faChevronUp, faThumbtack, faTrash, faPen, faThumbtackSlash } from '@fortawesome/free-solid-svg-icons';
import TimelineNoteForm from './TimelineNoteForm';
import { useTranslation } from 'react-i18next';
import '../../style/scss/tooltip.scss';

interface TimelineNoteProps {
    placement: 'summary' | 'page';
    item: TimelineItemType;
    note: TimelineNoteType;
    timeline_page: string;
    timeline_page_id: number;
    createdBy?: UserType | null;
    isPinned?: boolean;
    isAnotherPinned?: boolean;
    onSubmitSuccess?: (newItem: TimelineItemType) => void;
}

const TimelineNote: React.FC<TimelineNoteProps> = ({ 
    placement, item, note, timeline_page, timeline_page_id, 
    isPinned, createdBy, isAnotherPinned, onSubmitSuccess
}) => {
    const { t } = useTranslation();
    const { userHash } = useAuthContext();
    const { setFloatingAlert } = useGlobalContext();
    const { initializeModal } = useModal();
    const [isExpanded, setIsExpanded] = useState(false);
    const [isExpandable, setIsExpandable] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const textRef = useRef<HTMLParagraphElement>(null);
    const helperTextRef = useRef<HTMLDivElement>(null);
    
    // Format the date
    const noteDate = formatTimelineDate(item.ordering_date, false);

    // Expand the note
    const toggleIsExpanded = () => {
        setIsExpanded(!isExpanded);
    };

    // Shows the text expand icon if the helpertext element is higher than the
    // visible text element, the measurement is repeated whenever the note gets
    // its layout so notes inside a summary or modal which render before they are
    // visible still get the expand icon
    useEffect(() => {
        if (isExpanded) return;

        // Compare the full text height with the clamped text height
        const measure = () => {
            if (textRef.current && helperTextRef.current) {
                const visibleTextHeight = textRef.current.offsetHeight;
                const helperTextHeight = helperTextRef.current.offsetHeight;
                if (visibleTextHeight > 0) setIsExpandable(helperTextHeight > visibleTextHeight);
            }
        };

        // Measure after the first paint and on every resize of the text element
        const timer = setTimeout(measure, 100);
        const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
        if (resizeObserver && helperTextRef.current) resizeObserver.observe(helperTextRef.current);
        if (resizeObserver && textRef.current) resizeObserver.observe(textRef.current);

        return () => {
            clearTimeout(timer);
            resizeObserver?.disconnect();
        };
    }, [note, isExpanded, isEditing]);

    // Save the pin/unpin to the database
    const toggleIsPinned = async () => {
        try {
            // Configure the submit data
            const submitData = {
                pinned: !note.pinned,
                timeline_item: item.id,
                [timeline_page as string]: timeline_page_id,
                modified_by_user_hash: userHash,
            }

            // Send the pin status to the backend
            const response = await saveData({ 
                apiUrl: `patch_note/${note.timeline_object_id}`,
                method: 'patch',
                data: submitData
            })

            // Handle successful response
            if (response?.status === 200) {

                // Show success alert
                setFloatingAlert({ type: 'success' });

                // Update the timeline items state
                onSubmitSuccess?.({
                    ...response.data,
                    timeline_object_type: 'note'
                });
            }
        } catch (error) {
            console.error('Failed to update pinned state', error)
        }
    };

    // Handle editing of a task
    const handleEditing = () => {
        setIsEditing(true);

        if (placement === 'summary') {
            initializeModal(
                <TimelineNoteForm
                    placement='modal'
                    timeline_item_id={item.id}
                    timeline_page={timeline_page}
                    timeline_page_id={timeline_page_id}
                    noteId={item.timeline_object_id}
                    onSubmitSuccess={onSubmitSuccess}
                />, 
                { 
                    modalSize: 'small-medium',
                    title: t('timeline.note.general.edit_note_header'),
                    onClose: () => setIsEditing(false)
                }
            );
        }
    };

    // Delete the note in the database
    const deleteNote = async () => {
        try {
            // Call the delete note function on the backend
            const response = await saveData({ 
                apiUrl: `delete_note/${note.timeline_object_id}`,
                method: 'patch',
                data: {},
                source: axios.CancelToken.source()
            })

            // Handle successful response
            if (response && response.status === 200) {
                // Show success alert
                setFloatingAlert({ 'type': 'success' });

                // Update the timeline items state
                onSubmitSuccess?.({
                    ...response.data,
                    timeline_object_type: 'note'
                });
            }
        } catch (error) {
            setFloatingAlert({ type: 'danger', message: 'timeline.note.alert.delete_note_failed_message' })
        }
    };

    // Determine the pin tooltip text based on its state
    const pinTooltipText = isPinned ? 'timeline.note.general.unpin_note_label' : 'timeline.note.general.pin_note_label';

    // Determine the expand tooltip text based on its state
    const expandTooltipText = isExpanded ? 'general.shrink_label' : 'general.expand_label';

    return (
        <div className={`timeline-item note ${isPinned ? 'pinned' : ''}`}>
            {placement === 'page' && (
                <div className={`marker-container ${isPinned ? 'hide' : ''}`}>
                    <div className="big-marker">
                        <FontAwesomeIcon icon={faStickyNote} className='note-icon fa-fw' />
                    </div>
                </div>
            )}
            <div className={`note-content ${isEditing ? 'editing' : ''}`}>
                <div className='note-info'>
                    <div className='left-items'>
                        {noteDate}
                        {createdBy && createdBy.full_name && (
                            <>
                                <span style={{ margin: '0 5px' }}>•</span>
                                <span>{createdBy.full_name}</span>
                            </>
                        )}
                    </div>
                    <div className='right-items'>
                        {!isPinned && isExpandable && !isEditing &&
                            <div className='tooltip-icon expand-icon'
                                 onClick={toggleIsExpanded}>
                                    <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown}/>
                                    <span className="tooltip">{t(expandTooltipText)}</span>
                            </div> 
                        }
                        {!isAnotherPinned && !(isEditing && placement === 'page') &&
                            <div className='tooltip-icon pin-icon'
                                 onClick={toggleIsPinned}>
                                    <FontAwesomeIcon icon={isPinned ? faThumbtackSlash : faThumbtack} />
                                    <span className="tooltip">{t(pinTooltipText)}</span>
                            </div>
                        }
                        {placement === 'summary' && (
                            <div className='tooltip-icon edit-icon'
                                 onClick={handleEditing}>
                                <FontAwesomeIcon icon={faPen} />
                                <span className="tooltip">{t('general.edit_label')}</span>
                            </div>
                        )}
                        {placement === 'page' && !isEditing &&(
                            <>
                                <div className='tooltip-icon edit-icon'
                                     onClick={handleEditing}>
                                        <FontAwesomeIcon icon={faPen} />
                                        <span className="tooltip">{t('general.edit_label')}</span>
                                </div>
                                <div className='tooltip-icon delete-icon'
                                     onClick={deleteNote}>
                                        <FontAwesomeIcon icon={faTrash} />
                                        <span className="tooltip">{t('general.delete')}</span>
                                </div>  
                            </>
                        )}
                    </div>
                </div>
                {isEditing && placement === 'page' ? (
                    <TimelineNoteForm
                        placement='inline'
                        timeline_item_id={item.id}
                        timeline_page={timeline_page}
                        timeline_page_id={timeline_page_id}
                        noteId={item.timeline_object_id}
                        closeForm={() => setIsEditing(false)}
                        onSubmitSuccess={onSubmitSuccess}
                    />
                ) : (item.note && 
                    <div style={{ position: 'relative' }}>
                        <p ref={textRef} 
                           className='note-text' 
                           style={!isPinned && !isExpanded ? { overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: '2', WebkitBoxOrient: 'vertical', whiteSpace: 'pre-wrap', wordBreak: 'break-word' } : { whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                           dangerouslySetInnerHTML={{ __html: item.note.note }}>
                        </p>
                        {/* Help element to determine if the text expand icon needs to show */}
                        <p ref={helperTextRef} 
                           className='note-text' 
                           style={{ position: 'absolute', visibility: 'hidden', height: 'auto', width: '100%', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                           dangerouslySetInnerHTML={{ __html: item.note.note }}>
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TimelineNote;